import { Injectable, inject } from "@angular/core";
import {
  Auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  User as FbUser,
} from "@angular/fire/auth";
import {
  docData,
  getDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from "@angular/fire/firestore";
import { Observable } from "rxjs";
import { FirestoreService } from "./firestore.service";
import { AppUser, COL } from "./types";
import { AppUserUi } from "../interface/user";

export interface BootstrapSuperAdminInput {
  email: string;
  password: string;
  givenName?: string;
  familyName?: string;
}

/**
 * Decorates an AppUser with the legacy `Person.*` aliases consumed by
 * existing templates. Pure function — no side effects. Storage stays flat;
 * only the in-memory copy gets the nested shape rebuilt for the UI.
 */
function withLegacyAliases(u: AppUser): AppUserUi {
  return {
    ...u,
    Person: {
      Name_Person: u.name ?? "",
      Lastname_Person: u.lastname ?? "",
      DocumentId_Person: u.documentId ?? null,
      BirthDate_Person: u.birthdate ?? null,
    },
  };
}

/**
 * UsersService — reads + writes for the `users/{uid}` collection. The doc id
 * is the Firebase Auth UID, so we can fetch it directly without queries.
 */
@Injectable({ providedIn: "root" })
export class UsersService {
  private readonly fs = inject(FirestoreService);
  private readonly auth = inject(Auth);

  /** Live stream of the user doc. Emits `null` when there's no doc yet. */
  user$(uid: string): Observable<AppUserUi | null> {
    const ref = this.fs.docRef<AppUser>(COL.users, uid);
    return new Observable<AppUserUi | null>((sub) => {
      const inner = docData(ref, { idField: "id" }) as Observable<AppUser | null>;
      const s = inner.subscribe({
        next: (u) => sub.next(u ? withLegacyAliases(u) : null),
        error: (e) => sub.error(e),
        complete: () => sub.complete(),
      });
      return () => s.unsubscribe();
    });
  }

  async getUser(uid: string): Promise<AppUserUi | null> {
    const ref = this.fs.docRef<AppUser>(COL.users, uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const raw = { id: snap.id, ...(snap.data() as Omit<AppUser, "id">) };
    return withLegacyAliases(raw);
  }

  /**
   * Merge-writes a partial update into `users/{uid}`. Never reads first —
   * relies on Firestore's `merge: true` to leave existing fields untouched.
   * Use this for in-place edits (dark-mode toggle, profile updates, ...).
   */
  async mergeUserDoc(uid: string, partial: Partial<AppUser>): Promise<void> {
    const ref = this.fs.docRef<AppUser>(COL.users, uid);
    await setDoc(
      ref,
      { ...partial, updatedAt: serverTimestamp() } as any,
      { merge: true }
    );
  }

  /**
   * Initializes a fresh `users/{uid}` doc with canonical defaults plus the
   * caller's overrides. Used only by the bootstrap flow.
   */
  async initializeUserDoc(
    uid: string,
    partial: Partial<AppUser>
  ): Promise<void> {
    const ref = this.fs.docRef<AppUser>(COL.users, uid);
    const now = serverTimestamp() as unknown as Timestamp;
    const baseDefaults: Partial<AppUser> = {
      darkMode: false,
      notifyMail: true,
      isSuperAdmin: false,
      createdAt: now,
    };
    await setDoc(
      ref,
      { ...baseDefaults, ...partial, updatedAt: now } as any,
      { merge: true }
    );
  }

  /**
   * One-shot bootstrap: creates a Firebase Auth account (email/password),
   * writes a `users/{uid}` doc with `isSuperAdmin: true`, and signs the new
   * user in. Idempotent if the email already exists in Auth: it just signs
   * in and ensures the Firestore doc has the SuperAdmin flag.
   */
  async bootstrapSuperAdmin(input: BootstrapSuperAdminInput): Promise<FbUser> {
    let cred;
    try {
      cred = await createUserWithEmailAndPassword(
        this.auth,
        input.email,
        input.password
      );
    } catch (err: any) {
      if (err?.code === "auth/email-already-in-use") {
        cred = await signInWithEmailAndPassword(
          this.auth,
          input.email,
          input.password
        );
      } else {
        throw err;
      }
    }

    const uid = cred.user.uid;
    await this.initializeUserDoc(uid, {
      email: input.email,
      givenName: input.givenName ?? null,
      familyName: input.familyName ?? null,
      name: input.givenName ?? null,
      lastname: input.familyName ?? null,
      isSuperAdmin: true,
    });

    return cred.user;
  }
}
